import { Button } from '../primitives/button.js'
import { TextField, Typography } from '../primitives/index.js'
import { logger } from '@tunasong/models'
import { generateCommandSpecsFromCustomCommand, useEntityCommandsFromSpecs } from '@tunasong/plugin-lib'
import type { CustomCommandSpec, EntityCommandSpec } from '@tunasong/schemas'
import React, { useEffect, useState } from 'react'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { ZodError } from 'zod'
import { ButtonGroup } from '../primitives/index.js'

export interface CommandEditorProps {
  command?: CustomCommandSpec | null
  onChange(command: CustomCommandSpec): void
}

export const CommandEditor: FC<CommandEditorProps> = props => {
  const { command, onChange } = props

  const inputRef = React.useRef<HTMLInputElement>(null)
  const handleSave = () => {
    const value = inputRef.current?.value
    invariant(command, 'command is null')
    invariant(value, 'inputRef.current is null')
    onChange({ ...command, jsCode: value })
  }
  const [error, setError] = useState<ZodError>()
  const [output, setOutput] = useState<EntityCommandSpec[]>()
  const commands = useEntityCommandsFromSpecs(output)

  const handleRun = async () => {
    invariant(commands)
    // Run the commands
    for (const command of commands) {
      if (!command.cmd) {
        continue
      }
      logger.debug('Running command', command.id, command.name)
      await command.cmd()
    }
  }

  const handleValidate = (ev: React.ChangeEvent<HTMLInputElement>) => {
    // Validate that these are valid JS commands
    logger.debug('Validating custom command', ev.target.value)
    const code = ev.target.value
    if (!code) {
      return
    }

    /**
     * Run the custom command in a sandboxed environment
     */
    const result = generateCommandSpecsFromCustomCommand({
      code,
      scope: {
        targetEntity: {},
        cmdEntity: {},
        cmdParams: {},
      },
    })
    if (result.success) {
      setError(undefined)
      setOutput(result.data)
    } else {
      setError(result.error)
      setOutput(undefined)
    }
  }
  // Validate the command on load
  useEffect(() => {
    handleValidate({ target: { value: command?.jsCode ?? '' } } as never)
  }, [command?.jsCode])

  return (
    <div>
      <Typography variant="caption">
        Custom commands are Javascript run in a sandboxed environment. The following variables are available:
        targetEntity, cmdEntity, cmdParams
      </Typography>
      <TextField
        ref={inputRef}
        disabled={!command}
        style={{ marginTop: 16, marginBottom: 16 }}
        id={`${command?.commandId}-custom-command`}
        defaultValue={command?.jsCode}
        error={Boolean(error)}
        inputProps={{
          style: { fontFamily: 'monospace' },
        }}
        onChange={handleValidate}
        multiline
        fullWidth
        rows={5}
      />
      {Boolean(error) ? (
        <Typography style={{ marginTop: 16, marginBottom: 16 }} variant="caption" color={'error'} component={'div'}>
          {error?.message}
        </Typography>
      ) : null}
      {Boolean(output) ? (
        <Typography style={{ marginTop: 16, marginBottom: 16 }} variant="caption" color={'success'} component={'div'}>
          {JSON.stringify(output, null, 2)}
        </Typography>
      ) : null}

      <ButtonGroup style={{ width: '100%' }}>
        <Button disabled={!commands} onClick={handleRun}>
          Run commands
        </Button>
        <Button disabled={Boolean(error) || !command} onClick={handleSave}>
          Save
        </Button>
      </ButtonGroup>
    </div>
  )
}

export default CommandEditor
